import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, ExternalLink, RefreshCw } from "lucide-react";
import { API } from "../config/api";

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
};

/**
 * Trump Trades panel — President Donald J. Trump & First Lady Melania Trump
 * OGE Form 278-T periodic transactions, ingested from extapps2.oge.gov.
 *
 * Default view = ACTIONABLE TICKERS ONLY (equities + ETFs).
 * Bonds (muni/corp) are filtered out of the default view but visible via the
 * "Full transparency" toggle as requested by the user.
 */
export default function TrumpTrades() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [assetClass, setAssetClass] = useState("equity_etf"); // 'equity_etf' | 'all'
  const [filerFilter, setFilerFilter] = useState("all");      // 'all' | 'donald' | 'melania'
  const [refreshing, setRefreshing] = useState(false);
  const [filings, setFilings] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [tradesRes, filingsRes] = await Promise.allSettled([
        axios.get(
          `${API}/trump-trades?asset_class=${assetClass}&filer=${filerFilter}&limit=1000`,
          { timeout: 30000 },
        ),
        axios.get(`${API}/trump-trades/filings`, { timeout: 15000 }),
      ]);
      if (tradesRes.status === "fulfilled") setData(tradesRes.value.data);
      else setErr(tradesRes.reason?.message || "Failed to load Trump trades");
      if (filingsRes.status === "fulfilled") setFilings(filingsRes.value.data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assetClass, filerFilter]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/trump-trades/refresh`, null, { timeout: 180000 });
      await load();
    } catch (e) {
      // best effort
    } finally {
      setRefreshing(false);
    }
  };

  const rows = data?.rows || [];
  const summary = data?.summary_by_asset_class || {};

  const totals = useMemo(() => ({
    equity: summary.equity?.count || 0,
    etf: summary.etf?.count || 0,
    bond_muni: summary.bond_muni?.count || 0,
    bond_corp: summary.bond_corp?.count || 0,
  }), [summary]);

  if (loading && !data) {
    return (
      <div className="min-h-[300px] flex items-center justify-center text-slate-300">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading Trump filings…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="trump-trades-panel">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="flex-row items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              🇺🇸 Trump Trades — OGE Form 278-T
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1 max-w-3xl">
              Periodic transactions disclosed by{" "}
              <span className="text-slate-200 font-semibold">President Donald J. Trump</span> &amp;{" "}
              <span className="text-slate-200 font-semibold">First Lady Melania Trump</span> to the
              U.S. Office of Government Ethics. Sourced live from{" "}
              <span className="text-slate-300 font-mono">extapps2.oge.gov</span>. Default view shows
              actionable equity &amp; ETF tickers only — most of the filings are dominated by
              municipal &amp; corporate bonds, which you can reveal in full-transparency mode.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge variant="outline" className="border-slate-700 text-slate-300">
              {data?.n_filings_processed || 0} filings · {rows.length} rows shown
            </Badge>
            <button
              onClick={refresh}
              disabled={refreshing}
              data-testid="trump-refresh-btn"
              className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing OGE…" : "Refresh from OGE"}
            </button>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* Counts strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <CountTile label="Equity" value={totals.equity} tone="emerald" />
            <CountTile label="ETF" value={totals.etf} tone="sky" />
            <CountTile label="Muni Bond" value={totals.bond_muni} tone="amber" />
            <CountTile label="Corp Bond" value={totals.bond_corp} tone="rose" />
          </div>

          {/* Filter controls */}
          <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
            <div className="flex items-center gap-1" data-testid="trump-asset-filter">
              {[
                { k: "equity_etf", label: "Equity + ETF (actionable)" },
                { k: "equity", label: "Equity only" },
                { k: "etf", label: "ETF only" },
                { k: "all", label: "Full transparency (incl. bonds)" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  data-testid={`trump-asset-${opt.k}`}
                  onClick={() => setAssetClass(opt.k)}
                  className={`px-2 py-1 rounded border text-[11px] ${
                    assetClass === opt.k
                      ? "bg-emerald-700/50 border-emerald-500 text-white"
                      : "border-slate-700 text-slate-400 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1" data-testid="trump-filer-filter">
              {[
                { k: "all", label: "Donald + Melania" },
                { k: "donald", label: "Donald" },
                { k: "melania", label: "Melania" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  data-testid={`trump-filer-${opt.k}`}
                  onClick={() => setFilerFilter(opt.k)}
                  className={`px-2 py-1 rounded border text-[11px] ${
                    filerFilter === opt.k
                      ? "bg-sky-700/50 border-sky-500 text-white"
                      : "border-slate-700 text-slate-400 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {err && (
            <div className="text-xs text-red-300 bg-red-950/40 border border-red-900/60 p-2 rounded">
              {err}
            </div>
          )}

          {/* Trade table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trump-trades-table">
              <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-700">
                <tr>
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Filer</th>
                  <th className="text-left py-2 px-2">Ticker</th>
                  <th className="text-left py-2 px-2">Description</th>
                  <th className="text-left py-2 px-2">Action</th>
                  <th className="text-left py-2 px-2">Type</th>
                  <th className="text-right py-2 px-2">Amount band</th>
                  <th className="text-right py-2 px-2">≈ Midpoint</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-6 text-slate-500">
                      No rows in this view. Try the “Full transparency” filter
                      to see municipal &amp; corporate bonds.
                    </td>
                  </tr>
                )}
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    className="border-b border-slate-900 hover:bg-slate-800/40"
                    data-testid={`trump-row-${i}`}
                  >
                    <td className="py-1.5 px-2 text-slate-400 font-mono text-[11px]">
                      {r.tx_date}
                      {r.tx_date_raw && (
                        <span
                          className="ml-1 text-[9px] text-amber-400 cursor-help"
                          title={`OCR corrected from ${r.tx_date_raw}`}
                          data-testid={`trump-date-corrected-${i}`}
                        >
                          ⚠
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-slate-300 text-[11px]">
                      {r.filer === "Melania Trump" ? (
                        <span className="text-pink-300">Melania</span>
                      ) : (
                        <span className="text-amber-300">Donald</span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 font-mono">
                      {r.ticker ? (
                        <a
                          href={`/stock?ticker=${r.ticker}`}
                          className="text-emerald-300 hover:underline font-bold"
                          data-testid={`trump-ticker-${r.ticker}`}
                        >
                          {r.ticker}
                        </a>
                      ) : (
                        <span className="text-slate-600 text-[11px]">—</span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-slate-300 text-[11px] max-w-[420px] truncate" title={r.description}>
                      {r.description}
                    </td>
                    <td className="py-1.5 px-2">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        r.action === "BUY"
                          ? "bg-emerald-900/50 text-emerald-300"
                          : r.action === "SELL"
                          ? "bg-red-900/50 text-red-300"
                          : "bg-slate-800 text-slate-400"
                      }`}>
                        {r.action}
                      </span>
                    </td>
                    <td className="py-1.5 px-2">
                      <AssetBadge cls={r.asset_class} />
                    </td>
                    <td className="py-1.5 px-2 text-right text-slate-300 font-mono text-[11px]">
                      ${r.amount_low?.toLocaleString()}–${r.amount_high?.toLocaleString()}
                    </td>
                    <td className="py-1.5 px-2 text-right text-slate-400 text-[11px]">
                      ≈ {fmtMoney(r.amount_mid)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Filings provenance */}
      {filings?.rows?.length > 0 && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <FileText className="w-4 h-4" /> Source filings ({filings.count})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {filings.rows.map((f) => (
                <a
                  key={f.doc_id}
                  href={f.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-xs text-slate-300 hover:text-emerald-300"
                  data-testid={`trump-filing-${f.doc_id?.slice(0,8)}`}
                >
                  <ExternalLink className="w-3 h-3" />
                  <span className="font-mono">{f.filer || "—"}</span>
                  <span className="text-slate-500">·</span>
                  <span>{f.oge_received || "?"}</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-slate-400">{f.trade_count} transactions</span>
                  {f.ocr_used && (
                    <Badge variant="outline" className="text-[9px] border-amber-700 text-amber-300">
                      OCR
                    </Badge>
                  )}
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CountTile({ label, value, tone }) {
  const cls =
    tone === "emerald"
      ? "border-emerald-700/50 bg-emerald-900/20 text-emerald-200"
      : tone === "sky"
      ? "border-sky-700/50 bg-sky-900/20 text-sky-200"
      : tone === "amber"
      ? "border-amber-700/50 bg-amber-900/20 text-amber-200"
      : "border-rose-700/50 bg-rose-900/20 text-rose-200";
  return (
    <div className={`rounded border px-3 py-2 ${cls}`}>
      <div className="text-[10px] uppercase opacity-80">{label}</div>
      <div className="text-lg font-bold tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}

function AssetBadge({ cls }) {
  const map = {
    equity: { txt: "Equity", c: "bg-emerald-900/40 text-emerald-300" },
    etf: { txt: "ETF", c: "bg-sky-900/40 text-sky-300" },
    bond_muni: { txt: "Muni Bond", c: "bg-amber-900/40 text-amber-300" },
    bond_corp: { txt: "Corp Bond", c: "bg-rose-900/40 text-rose-300" },
    other: { txt: "Other", c: "bg-slate-800 text-slate-300" },
  };
  const v = map[cls] || map.other;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${v.c}`}>
      {v.txt}
    </span>
  );
}
